import { ChangeEventHandler, useState } from "react";
import Box from "@mui/material/Box";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEnhancedCustomerCreate } from "../data/Settings/useEnhancedCustomerCreate";
import { Snackbar } from "../shared/Snackbar";
import { CustomerType } from "./CustomerType";

interface CustomerSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomerUpdated: () => void;
}

export function CustomerSetupModal(props: CustomerSetupModalProps) {
  const { t } = useTranslation();
  const { isOpen, onClose, onCustomerUpdated } = props;
  const [createCustomerName, setCreateCustomerName] = useState("");
  const [createCustomerType, setCreateCustomerType] = useState<CustomerType>();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const customerTypeOptions = Object.values(CustomerType).map((value) => ({
    value,
    label: t(`settings.customerTypeOptions.${value.toLowerCase()}`),
  }));

  const isCustomerType = (value: string): value is CustomerType => {
    return customerTypeOptions.some((option) => option.value === value);
  };

  const handlerCustomerTypeChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    if (isCustomerType(value)) {
      setCreateCustomerType(value);
    }
  };

  const handleDialogClose = () => {
    setCreateCustomerName("");
    setCreateCustomerType(undefined);
    onClose();
  };

  const enhancedCustomerCreate = useEnhancedCustomerCreate({
    onSuccess: () => {
      setSnackbarText(t("settings.customerCreatedSuccess"));
      setShowSnackbar(true);
      setCreateCustomerName("");
      setCreateCustomerType(undefined);
      onCustomerUpdated();
    },
    onError: () => {
      setSnackbarText(t("common.dataCreateError", { val: "$t(customer.customer)" }));
      setShowSnackbar(true);
    },
  });

  function handleCreate() {
    if (!createCustomerName.trim()) {
      setSnackbarText(t("common.pleaseEnter", { val: t("settings.customerName") }));
      setShowSnackbar(true);
      return;
    }

    if (!createCustomerType) {
      setSnackbarText(t("common.pleaseEnter", { val: t("settings.customerType") }));
      setShowSnackbar(true);
      return;
    }

    enhancedCustomerCreate.mutate({
      name: createCustomerName.trim(),
      customerType: createCustomerType,
      active: true,
      addressId: null,
    });
  }

  const isCreating = enhancedCustomerCreate.isLoading;

  return (
    <Dialog open={isOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("settings.customerSetup")}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <Stack spacing={2} alignItems={"center"}>
            <TextField
              label={t("settings.customerName")}
              value={createCustomerName}
              onChange={(event) => setCreateCustomerName(event.target.value)}
              sx={{ width: 320 }}
            />

            <TextField
              select
              label={t("settings.customerType")}
              value={createCustomerType}
              onChange={handlerCustomerTypeChange}
              sx={{ width: 320 }}
            >
              {customerTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleDialogClose}>
          {t("common.close")}
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={isCreating}>
          {t("common.create")}
        </Button>
      </DialogActions>
      <Snackbar
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
        }}
        message={snackbarText}
      />
    </Dialog>
  );
}
